import { getItemFields } from '@mimir/web/util/api';
import { type ConstructedOngoingDownload, getId, type BuiltinOngoingDownload } from '@mimir/client-common/model/ongoingDownload';
import { api_getItemAncestry } from '@mimir/client-common/api/folderApi';
import { getItemLocalDirs } from '@mimir/client-common/util/folder';
import type { DownloadProgressDto } from '@mimir/common/dto/transferRequestDtos';
import { invariant } from '@mimir/common/util/invariant';
import { isFileAvailableLocally } from '@mimir/common/util/fs';
import { copyFile } from '@mimir/common/util/fs';
import type { LoadedItem } from '@mimir/common/dto/searchResultHit';
import type { CancelToken } from 'axios';
import type { DownloadAndMuxTask, ProgressHandler } from '@mimir/client-common/download/downloadAndMuxTask';
import { downloadProgressDtoFromProgressEntry } from '@mimir/client-common/download/downloadAndMuxTask';
import type { Logger } from '@mimir/common/types/logger';
import { globalBackendClient } from '@mimir/client-common/globals/backendClient';
import { cancelOngoingDownloadWithoutToast } from '../globals/cancelOngoingDownload';
import { localDownloadState } from '../globals/localDownloads';
import { tryImport } from './tryImport';
import { globalDownloadDeduplicator } from '../globals/downloadDeduplicator';
import type { AdobePanelContext, ApplicationContext, ElectronContext } from '@mimir/common/context/applicationContext';
import type { NotificationOps } from '@mimir/client-common/fileSync/notificationOps';
import type { OngoingDownloadOps } from '@mimir/client-common/fileSync/ongoingDownloadOps';
import type { LocalStateOps } from '@mimir/client-common/fileSync/localStateOps';
import { getDirname } from '@mimir/common/platformAbstraction/pathComposition';
import { makeDirWith } from '@mimir/client-common/upload/premiere';
import type { ProgressEntry } from '@mimir/primitive/structuredLogger';
import type { DownloadProgress } from '@mimir/common/types/downloader';
import type { ExplicitArtifactReference } from '@mimir/client-common/download/downloadItemArtifact';
import { downloadItemArtifact } from '@mimir/client-common/download/downloadItemArtifact';
import { DefaultBackendOps } from '@mimir/client-common/util/defaultBackendOperations';
import { makeStaticUrlProvider } from '@mimir/common/io/urlProvider';
import { syncLocalMediaState } from './syncing';
import { getBuiltinDownloadDestinationPath } from '@mimir/client-common/fileSync/downloadDestinationUtils';

const commonItemFieldsRequiredForDownload: (keyof LoadedItem)[] = ['id', 'hasEditProxy', 'highRes', 'editProxyUrl', 'hasProxy', 'proxy', 'folderParents', 'originalFileName', 'itemType', 'itemState'];

const neededItemFields: (keyof LoadedItem)[] = [...commonItemFieldsRequiredForDownload, 'media', 'editProxyFormat'];
type DownloadItem = Pick<LoadedItem, typeof neededItemFields[number]>;

export async function finishOngoingDownload(
  services: {
    applicationContext: AdobePanelContext | ElectronContext,
    notificationOps: NotificationOps,
    logger: Logger,
    ongoingDownloadOps: OngoingDownloadOps,
    localStateOps: LocalStateOps,
  },
  config: {
    downloadPath: string,
    userDataSystemPath: string,
    downloadToSubfolderPaths: boolean,
    shouldUsePremierePreferences: boolean,
    isProxyMode: boolean,
    skippedFileExtensions: string[],
  },
  ongoingDownload: BuiltinOngoingDownload,
) {
  if (ongoingDownload.artefact === 'proxy') {
    await processProxyDownload(services, config, ongoingDownload);
    return;
  }

  if (ongoingDownload.artefact === 'highres') {
    await processHighresDownload(services, config, ongoingDownload);
    return;
  }

  if (ongoingDownload.artefact === 'aaf') {
    await processAafDownload(services, ongoingDownload);
    return;
  }

  throw new Error('finishOngoingDownload not implemented for ' + ongoingDownload.artefact);
}

type StrictDownload = BuiltinOngoingDownload & Required<Pick<BuiltinOngoingDownload, 'itemId' | 'downloadDestination'>>;

function requireStrictActiveDownload(dl: BuiltinOngoingDownload): StrictDownload {
  invariant(dl.itemId, 'Expected ongoing download item id');
  invariant(dl.downloadDestination, 'Expected ongoing download downloadDestination');
  dl.cancelTokenSource.token.throwIfRequested();
  return dl as StrictDownload;
}

async function getDownloadItem(download: StrictDownload): Promise<DownloadItem> {
  const item: DownloadItem = await getItemFields(download.itemId, neededItemFields);
  return item;
}

async function processProxyDownload(
  services: {
    applicationContext: AdobePanelContext | ElectronContext,
    notificationOps: NotificationOps,
    logger: Logger,
    ongoingDownloadOps: OngoingDownloadOps,
    localStateOps: LocalStateOps,
  },
  config: {
    downloadPath: string,
    userDataSystemPath: string,
    downloadToSubfolderPaths: boolean,
    shouldUsePremierePreferences: boolean
    isProxyMode: boolean,
    skippedFileExtensions: string[],
  },
  dl: BuiltinOngoingDownload,
) {
  const ongoingDownload = requireStrictActiveDownload(dl);

  ongoingDownload.yetToBeSentToServer = false;
  const item = await getDownloadItem(ongoingDownload);

  if (await tryImportFromSharedMediaPath(services, config, ongoingDownload, item)) return;

  return downloadAndMux(services, ongoingDownload, 'proxy', item);
}


async function downloadAndMux(
  services: {
    applicationContext: AdobePanelContext | ElectronContext,
    logger: Logger,
    ongoingDownloadOps: OngoingDownloadOps,
    localStateOps: LocalStateOps,
  },
  ongoingDownload: StrictDownload,
  _artefact: 'highres' | 'proxy', // <- TODO: Remove unused
  item: Pick<LoadedItem, typeof neededItemFields[number]>,
) {
  const downloadDestinationPath = getBuiltinDownloadDestinationPath(ongoingDownload.downloadDestination);
  const folderPath = getDirname(downloadDestinationPath, services.applicationContext.pathSep);
  await makeDirWith(services, folderPath);

  let task = makeDownloadAndMuxTask(services, ongoingDownload);

  task = globalDownloadDeduplicator.deduplicate(getId(ongoingDownload.dlId), task);
  await runDownloadAndMuxTaskAndUpdateUi(services, task, item, ongoingDownload);
}

function getExplicitArtifactReferenceFromOngoingDownload(
  ongoingDownload: StrictDownload,
): ExplicitArtifactReference {
  if (ongoingDownload.dlId.artifactReference.type !== 'ImplicitReference') {
    return ongoingDownload.dlId.artifactReference;
  }

  if (!ongoingDownload.isSegmentedDownload) {
    return {
      type: 'FlatFileReference',
      itemId: ongoingDownload.dlId.itemId,
    };
  } else {
    invariant(ongoingDownload.lastSegmentIndex !== undefined, 'Expected lastSegmentIndex to be defined');
    invariant((ongoingDownload.firstSegmentIndex ?? 0) === 0, 'Expected firstSegmentIndex to be zero for truncated Mp4');
    return {
      type: 'TruncatedMp4',
      itemId: ongoingDownload.dlId.itemId,
      lastSegmentIndex: ongoingDownload.lastSegmentIndex,
    };
  }
}

function makeDownloadAndMuxTask(
  services: {
    applicationContext: AdobePanelContext | ElectronContext,
    logger: Logger,
  },
  ongoingDownload: StrictDownload,
): DownloadAndMuxTask {

  return async (onProgress: ProgressHandler, ct: CancelToken) => {
    const { logger } = services;
    const outputFilename = getBuiltinDownloadDestinationPath(ongoingDownload.downloadDestination);
    const abortController = new AbortController();
    void ct.promise.then(() => abortController.abort());
    const startTime = Date.now();
    const artifactReference : ExplicitArtifactReference = getExplicitArtifactReferenceFromOngoingDownload(ongoingDownload);
    const itemArtefact = ongoingDownload.dlId.artefact;
    const downloader = services.applicationContext.downloader;
    const backendOps = new DefaultBackendOps(services.applicationContext.backendClient);

    logger.log({ level: 'info', message: 'Download', artifactReference, itemArtefact });

    await downloadItemArtifact(
      {
        downloader,
        backendOps,
      },
      artifactReference,
      itemArtefact,
      outputFilename,
      abortController.signal,
      (progress: DownloadProgress) => {
        if (progress.totalNumberOfBytes === 'undetermined') {
          // TODO: Propagate something in this scenario?
          return;
        }
        const progressEntry : ProgressEntry = {
          position: progress.numberOfBytesDownloaded,
          length: progress.totalNumberOfBytes,
        };
        const dto = downloadProgressDtoFromProgressEntry(progressEntry, startTime);
        if (dto !== null) { onProgress(dto); }
      }
    );
  };
}

async function runDownloadAndMuxTaskAndUpdateUi(
  services: {
    applicationContext: ApplicationContext,
    logger: Logger,
    ongoingDownloadOps: OngoingDownloadOps,
    localStateOps: LocalStateOps,
  },
  task: DownloadAndMuxTask,
  item: Pick<LoadedItem, typeof neededItemFields[number]>,
  ongoingDownload: Readonly<ConstructedOngoingDownload>,
) {
  const cts = ongoingDownload.cancelTokenSource;
  const onProgress: ProgressHandler = async (downloadStats: DownloadProgressDto) => {
    const percentage = 100 * downloadStats.transferred / downloadStats.totalSize;
    for (const reporter of ongoingDownload.reporters ?? []) {
      reporter.onProgress(percentage);
    }
  };

  await task(onProgress, cts.token);

  for (const reporter of ongoingDownload.reporters ?? []) {
    reporter.onProgress(100);
  }

  await syncLocalMediaState(
    services,
    {
      downloadIdentity: ongoingDownload.dlId,
      item,
      subclip: null,
    }
  );
}


async function processAafDownload(
  services: {
    applicationContext: AdobePanelContext | ElectronContext,
    logger: Logger,
    ongoingDownloadOps: OngoingDownloadOps,
    localStateOps: LocalStateOps,
  },
  ongoingDownload: BuiltinOngoingDownload,
) {
  if (ongoingDownload === undefined || ongoingDownload.itemId === undefined) {
    return;
  }

  ongoingDownload.cancelTokenSource.token.throwIfRequested();

  ongoingDownload.yetToBeSentToServer = false;
  const item = await getItemFields(ongoingDownload.itemId, [...commonItemFieldsRequiredForDownload, 'aaf']);

  const url = item.aaf;
  if (!url) {
    services.logger.info('Missing download source URL for aaf');
    return;
  }

  const downloadDestinationPath = getBuiltinDownloadDestinationPath(ongoingDownload.downloadDestination);
  const folderPath = getDirname(downloadDestinationPath, services.applicationContext.pathSep);
  await makeDirWith(services, folderPath);

  const abortController = new AbortController();

  ongoingDownload.cancelTokenSource.token.promise
    .then(() => abortController.abort());

  await services.applicationContext.downloader.downloadFlatFile({
    urlProvider: makeStaticUrlProvider(url),
    outputFilename: downloadDestinationPath,
    abortSignal: abortController.signal,
    progressHandler: (downloadProgress: DownloadProgress) => {
      if (downloadProgress.totalNumberOfBytes === 'undetermined') {
        return;
      }
      const percentage = 100 * downloadProgress.numberOfBytesDownloaded / downloadProgress.totalNumberOfBytes;
      for (const reporter of ongoingDownload.reporters ?? []) {
        reporter.onProgress(percentage);
      }
    }
  });
}


async function processHighresDownload(
  services: {
    applicationContext: AdobePanelContext | ElectronContext,
    notificationOps: NotificationOps,
    logger: Logger,
    ongoingDownloadOps: OngoingDownloadOps,
    localStateOps: LocalStateOps,
  },
  config: {
    downloadPath: string,
    userDataSystemPath: string,
    downloadToSubfolderPaths: boolean,
    shouldUsePremierePreferences: boolean,
    isProxyMode: boolean,
    skippedFileExtensions: string[],
  },
  dl: BuiltinOngoingDownload,
) {
  const ongoingDownload = requireStrictActiveDownload(dl);
  const item = await getDownloadItem(ongoingDownload);

  if (await tryImportFromSharedMediaPath(services, config, ongoingDownload, item)) return;

  return downloadAndMux(
    services,
    ongoingDownload,
    'highres',
    item
  );
}


async function tryImportFromSharedMediaPath(
  services: {
    applicationContext: AdobePanelContext | ElectronContext,
    notificationOps: NotificationOps,
    logger: Logger,
    ongoingDownloadOps: OngoingDownloadOps,
    localStateOps: LocalStateOps,
  },
  config: {
    downloadPath: string,
    userDataSystemPath: string,
    shouldUsePremierePreferences: boolean,
    skippedFileExtensions: string[],
  },
  ongoingDownload: StrictDownload,
  item: DownloadItem
) {
  const downloadDestinationPath = getBuiltinDownloadDestinationPath(ongoingDownload.downloadDestination);
  const folderPath = getDirname(downloadDestinationPath, services.applicationContext.pathSep);
  await makeDirWith(services, folderPath);

  // Search for item in shared folder if configured
  if (localDownloadState.sharedDownloadPath) {
    // First check root folder
    let sharedPath = downloadDestinationPath.replace(localDownloadState.downloadPath, localDownloadState.sharedDownloadPath);
    if (await isFileAvailableLocally(services.applicationContext.nodeApi, services.logger, sharedPath)) {
      await copyFile(services.applicationContext.nodeApi, sharedPath, downloadDestinationPath);
      if (await tryImport(services, config, ongoingDownload, undefined)) {
        await cancelOngoingDownloadWithoutToast(services, ongoingDownload, config.skippedFileExtensions);
        return true;
      }
    }
    // Then check in folder structures
    const localDirs = await getItemLocalDirs(item, localDownloadState.sharedDownloadPath, services.applicationContext.pathSep,
      (itemId) => api_getItemAncestry(globalBackendClient, itemId, undefined));

    if (localDirs) {
      for (const dir of localDirs) {
        sharedPath = `${dir}${services.applicationContext.pathSep}${item.originalFileName}`;
        if (await isFileAvailableLocally(services.applicationContext.nodeApi, services.logger, sharedPath)) {
          await copyFile(services.applicationContext.nodeApi, sharedPath, downloadDestinationPath);
          if (await tryImport(services, config, ongoingDownload, undefined)) {
            await cancelOngoingDownloadWithoutToast(services, ongoingDownload, config.skippedFileExtensions);
            return true;
          }
        }
      }
    }
  }

  return false;
}

